/**
 * Bundle Promo — theme integration
 * ────────────────────────────────
 * Surfaces an active bundle_percentage campaign on PDPs (and inside
 * the vendor-cart card) so practitioners discover the bundle offer
 * BEFORE committing.
 *
 * Source of truth: GET /api/storefront/active-promos on BR Connect
 * returns the live bundle_percentage campaigns with bundleProductIds
 * + percentage. Cart state from /cart.js. cart:updated pubsub event
 * triggers re-renders.
 *
 * Read-only with one optional CTA: "Add [missing product]". Clicking
 * adds the missing product (resolved via /products/{id}.js → handle →
 * default variant) to cart. Once all bundle products are in cart, the
 * Shopify Function we deployed (handle: bundle-percentage) applies the
 * % off both at checkout.
 *
 * Sibling of bxgy-promo.js + min-qty-promo.js. Self-contained — see
 * min-qty-promo.js header for the "duplicate utilities" rationale.
 */
(function () {
  'use strict';

  var BR_CONNECT_BASE =
    window.__beautyRepConnectBase || 'https://beautyrepconnect.com';
  var ACTIVE_PROMOS_PATH = '/api/storefront/active-promos';
  var SOURCE_LABEL = 'bundle-promo';

  /* ────────────────────────────────────────
     Tiny utilities
  ──────────────────────────────────────── */

  function gidToNumericId(gid) {
    if (!gid || typeof gid !== 'string') return null;
    var m = gid.match(/(\d+)$/);
    return m ? m[1] : null;
  }

  function fetchJsonResilient(url, init, retriesLeft) {
    if (typeof retriesLeft !== 'number') retriesLeft = 2;
    var backoffs = [1500, 3000];
    var attempt = 2 - retriesLeft;
    return fetch(url, init)
      .then(function (res) {
        if (res.status === 429 && retriesLeft > 0) {
          var wait = backoffs[Math.min(attempt, backoffs.length - 1)];
          return new Promise(function (resolve) { setTimeout(resolve, wait); })
            .then(function () { return fetchJsonResilient(url, init, retriesLeft - 1); });
        }
        if (!res.ok) throw new Error(url + ' → ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        if (retriesLeft > 0) {
          var wait = backoffs[Math.min(attempt, backoffs.length - 1)];
          return new Promise(function (resolve) { setTimeout(resolve, wait); })
            .then(function () { return fetchJsonResilient(url, init, retriesLeft - 1); });
        }
        throw err;
      });
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = arguments;
      var ctx = this;
      if (t) clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  /* ────────────────────────────────────────
     State
  ──────────────────────────────────────── */

  var state = {
    promo: null,           // { campaignId, title, percentage, bundleNumericIds: Set<string>, bundleNumericIdList, endsAt }
    cartLines: [],
    productCache: {},      // numericId → { handle, title, variantId, price }
    handlesFromApi: {},    // numericId → handle (from /active-promos productHandles)
    productCards: {},      // numericId → ProductCard (richer — image/price/variantId)
    inflightCartFetch: null,
  };

  /* ────────────────────────────────────────
     Promo selection
  ──────────────────────────────────────── */

  function pickBundlePromo(allPromos, productNumericId, customerBrand) {
    if (!Array.isArray(allPromos)) return null;
    var matching = allPromos.filter(function (p) {
      if (p.type !== 'bundle_percentage') return false;
      if (p.audience && p.audience.brand && customerBrand && p.audience.brand !== customerBrand) {
        return false;
      }
      var bundleIds = (p.bundleProductIds || [])
        .map(gidToNumericId)
        .filter(Boolean);
      return bundleIds.indexOf(String(productNumericId)) !== -1;
    });
    if (!matching.length) return null;
    matching.sort(function (a, b) { return (b.percentage || 0) - (a.percentage || 0); });
    var best = matching[0];
    var bundleNumericIds = new Set();
    var bundleNumericIdList = [];
    (best.bundleProductIds || []).forEach(function (gid) {
      var n = gidToNumericId(gid);
      if (n) {
        bundleNumericIds.add(n);
        bundleNumericIdList.push(n);
      }
    });
    return {
      campaignId: best.campaignId,
      title: best.title,
      percentage: best.percentage,
      bundleNumericIds: bundleNumericIds,
      bundleNumericIdList: bundleNumericIdList,
      endsAt: best.endsAt,
    };
  }

  /**
   * Pick a bundle promo for a cart context (no PDP product). Used by
   * the cart-card + drawer renders. Picks the highest-% bundle whose
   * products overlap any cart line, scoped to the customer's brand.
   */
  function pickBundlePromoForCart(allPromos, customerBrand, cartLines) {
    if (!Array.isArray(allPromos)) return null;
    var cartProductIds = new Set();
    (cartLines || []).forEach(function (l) { cartProductIds.add(String(l.product_id)); });
    var matching = allPromos.filter(function (p) {
      if (p.type !== 'bundle_percentage') return false;
      if (p.audience && p.audience.brand && customerBrand && p.audience.brand !== customerBrand) {
        return false;
      }
      var bundleIds = (p.bundleProductIds || [])
        .map(gidToNumericId)
        .filter(Boolean);
      return bundleIds.some(function (id) { return cartProductIds.has(id); });
    });
    if (!matching.length) return null;
    matching.sort(function (a, b) { return (b.percentage || 0) - (a.percentage || 0); });
    var best = matching[0];
    var bundleNumericIds = new Set();
    var bundleNumericIdList = [];
    (best.bundleProductIds || []).forEach(function (gid) {
      var n = gidToNumericId(gid);
      if (n) {
        bundleNumericIds.add(n);
        bundleNumericIdList.push(n);
      }
    });
    return {
      campaignId: best.campaignId,
      title: best.title,
      percentage: best.percentage,
      bundleNumericIds: bundleNumericIds,
      bundleNumericIdList: bundleNumericIdList,
      endsAt: best.endsAt,
    };
  }

  /* ────────────────────────────────────────
     Cart fetch
  ──────────────────────────────────────── */

  function fetchCart() {
    if (state.inflightCartFetch) return state.inflightCartFetch;
    state.inflightCartFetch = fetchJsonResilient('/cart.js')
      .then(function (cart) {
        state.cartLines = (cart && cart.items) || [];
        state.inflightCartFetch = null;
        return cart;
      })
      .catch(function (err) {
        state.inflightCartFetch = null;
        console.warn('[' + SOURCE_LABEL + '] cart fetch failed:', err);
        return { items: state.cartLines };
      });
    return state.inflightCartFetch;
  }

  function productIdsInCart() {
    var ids = new Set();
    for (var i = 0; i < state.cartLines.length; i++) {
      ids.add(String(state.cartLines[i].product_id));
    }
    return ids;
  }

  /* ────────────────────────────────────────
     Product resolution (for the Add CTA)
  ──────────────────────────────────────── */

  // Shopify exposes /products/{handle}.js but not /products?id=N. To go
  // from numericId to handle we read from two places, preferring the
  // server-side resolution:
  //   1. state.handlesFromApi — the productHandles map embedded in
  //      /api/storefront/active-promos response (BR Connect joins to
  //      its inventory table). Authoritative for any product the API
  //      knows about.
  //   2. <script id="br-bundle-product-handles" type="application/json">
  //      — the per-page hint injected by the bundle-promo-pdp snippet.
  //      Covers the fallback case where vendor_products is missing
  //      a handle row for some reason.
  function getProductHandleHint(numericId) {
    var fromApi = state.handlesFromApi[String(numericId)];
    if (fromApi) return fromApi;
    var node = document.getElementById('br-bundle-product-handles');
    if (!node) return null;
    try {
      var map = JSON.parse(node.textContent || '{}');
      return map[String(numericId)] || null;
    } catch (e) {
      return null;
    }
  }

  function fetchProductByHandle(handle) {
    if (!handle) return Promise.reject(new Error('no handle'));
    return fetchJsonResilient('/products/' + handle + '.js')
      .then(function (p) {
        var variantId = (p.variants && p.variants[0] && p.variants[0].id) || null;
        return {
          numericId: String(p.id),
          handle: p.handle,
          title: p.title,
          variantId: variantId,
          price: p.price,
        };
      });
  }

  function ensureProductCached(numericId) {
    if (state.productCache[numericId]) {
      return Promise.resolve(state.productCache[numericId]);
    }
    var handle = getProductHandleHint(numericId);
    if (!handle) return Promise.resolve(null); // theme didn't render the hint
    return fetchProductByHandle(handle).then(function (info) {
      state.productCache[numericId] = info;
      return info;
    });
  }

  /* ────────────────────────────────────────
     Add to cart (Add bundle CTA)
  ──────────────────────────────────────── */

  function addVariantToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/add → ' + res.status);
      return res.json();
    });
  }

  /* ────────────────────────────────────────
     Render
  ──────────────────────────────────────── */

  function renderPdp() {
    var el = document.querySelector('[data-bundle-promo-pdp]');
    if (!el) return;
    if (!state.promo) {
      el.classList.add('hidden');
      return;
    }
    var promo = state.promo;
    var thisProductId = el.getAttribute('data-product-id');
    // Gate by PDP product membership: the promo may have been chosen by
    // the cart fallback (state.promo set via pickBundlePromoForCart
    // because some cart line is in the bundle). Without this check the
    // PDP banner would render on any product page while the cart has
    // any bundle product — even on products NOT in the bundle.
    if (thisProductId && !promo.bundleNumericIds.has(String(thisProductId))) {
      el.classList.add('hidden');
      return;
    }
    var inCart = productIdsInCart();
    var pctLabel = Math.round((promo.percentage || 0) * 100) + '%';

    // Find the OTHER bundle products (not the current PDP product) and
    // figure out which are missing from cart.
    var others = Array.from(promo.bundleNumericIds).filter(function (id) {
      return id !== String(thisProductId);
    });
    var missing = others.filter(function (id) { return !inCart.has(id); });

    el.classList.remove('hidden');
    var below = el.querySelector('[data-bundle-below]');
    var qualified = el.querySelector('[data-bundle-qualified]');
    var pctSlots = el.querySelectorAll('[data-bundle-pct]');
    pctSlots.forEach(function (s) { s.textContent = pctLabel; });
    // "See bundle" trigger — show in both states once promo resolved.
    var triggers = el.querySelectorAll('[data-bundle-see-products]');
    triggers.forEach(function (t) { t.classList.remove('hidden'); });

    if (missing.length === 0) {
      // Bundle complete (everything in cart, INCLUDING the current PDP
      // product if it was already added) — show the qualified state.
      // If the current PDP product ISN'T in cart yet but all others are,
      // we still show "Add to cart to complete bundle" via the below
      // state — but Shopify is what enforces. For UX we treat
      // "all-others-in-cart" as the trigger to nudge "add this one".
      if (inCart.has(String(thisProductId))) {
        if (below) below.classList.add('hidden');
        if (qualified) qualified.classList.remove('hidden');
      } else {
        // Show below state with "add this product" framing.
        if (below) below.classList.remove('hidden');
        if (qualified) qualified.classList.add('hidden');
        var msg = below && below.querySelector('[data-bundle-need-msg]');
        if (msg) msg.textContent = 'Add this to your cart to complete the bundle and save ' + pctLabel + '.';
        var addBtn = below && below.querySelector('[data-bundle-add-other]');
        if (addBtn) addBtn.classList.add('hidden');
      }
      return;
    }
    // At least one product missing. Render below state with CTA.
    if (below) below.classList.remove('hidden');
    if (qualified) qualified.classList.add('hidden');
    var addBtn2 = below && below.querySelector('[data-bundle-add-other]');
    var msg2 = below && below.querySelector('[data-bundle-need-msg]');
    if (missing.length === 1) {
      // Single missing product → resolve title for the CTA.
      ensureProductCached(missing[0]).then(function (info) {
        if (info && msg2) {
          msg2.textContent =
            'Combine with ' + info.title + ' and save ' + pctLabel + ' on both.';
        } else if (msg2) {
          msg2.textContent =
            'Add the matching product and save ' + pctLabel + ' on both.';
        }
        if (info && info.variantId && addBtn2) {
          addBtn2.classList.remove('hidden');
          addBtn2.textContent = 'Add ' + info.title + ' to cart';
          addBtn2.dataset.variantId = String(info.variantId);
        } else if (addBtn2) {
          addBtn2.classList.add('hidden');
        }
      });
    } else {
      // Multi-missing. Just show generic copy + hide the CTA (we'd need
      // multiple add buttons; not worth the complexity for the MSS May
      // case which only has 2-product bundles).
      if (msg2) {
        msg2.textContent =
          'Add ' + missing.length + ' more bundle items and save ' + pctLabel + ' on the set.';
      }
      if (addBtn2) addBtn2.classList.add('hidden');
    }
  }

  function renderAll() {
    renderPdp();
    renderCard();
  }

  /* ────────────────────────────────────────
     Render — vendor cart card + drawer banners
  ──────────────────────────────────────── */

  function bundleProductIdsInVendorCart(bundleNumericIds, vendor) {
    var present = new Set();
    for (var i = 0; i < state.cartLines.length; i++) {
      var line = state.cartLines[i];
      if (vendor && line.vendor !== vendor) continue;
      if (bundleNumericIds.has(String(line.product_id))) {
        present.add(String(line.product_id));
      }
    }
    return present;
  }

  function renderCard() {
    // Per-vendor scoping mirrors min-qty-promo.js — see its comment.
    // Cart card + drawer slots both expose [data-bundle-promo-card] so
    // one renderer feeds both surfaces.
    var els = document.querySelectorAll('[data-bundle-promo-card]');
    if (!els.length || !state.promo) return;
    var promo = state.promo;
    var pctLabel = Math.round((promo.percentage || 0) * 100) + '%';
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var vendor = el.getAttribute('data-vendor') || null;
      var presentInVendor = bundleProductIdsInVendorCart(
        promo.bundleNumericIds, vendor,
      );
      if (presentInVendor.size === 0) {
        // No bundle product in THIS vendor's cart — banner not relevant
        // (we don't want to nag the user about a Marini bundle on a
        // Babor cart card).
        el.classList.add('hidden');
        continue;
      }
      el.classList.remove('hidden');
      var below = el.querySelector('[data-bundle-below]');
      var qualified = el.querySelector('[data-bundle-qualified]');
      var pctSlots = el.querySelectorAll('[data-bundle-pct]');
      pctSlots.forEach(function (s) { s.textContent = pctLabel; });
      var triggers = el.querySelectorAll('[data-bundle-see-products]');
      triggers.forEach(function (t) { t.classList.remove('hidden'); });

      var allPresent = promo.bundleNumericIdList.every(function (id) {
        return presentInVendor.has(id);
      });
      if (allPresent) {
        if (below) below.classList.add('hidden');
        if (qualified) qualified.classList.remove('hidden');
        continue;
      }
      // At least one missing — render the below state.
      if (below) below.classList.remove('hidden');
      if (qualified) qualified.classList.add('hidden');
      var msg = below && below.querySelector('[data-bundle-need-msg]');
      var addBtn = below && below.querySelector('[data-bundle-add-other]');
      var missing = promo.bundleNumericIdList.filter(function (id) {
        return !presentInVendor.has(id);
      });
      if (missing.length === 1) {
        var missingId = missing[0];
        var card = state.productCards[missingId];
        if (msg) {
          msg.textContent = card
            ? 'Combine with ' + card.title + ' and save ' + pctLabel + ' on both.'
            : 'Add the matching product and save ' + pctLabel + ' on both.';
        }
        if (addBtn && card && card.variantId) {
          addBtn.classList.remove('hidden');
          addBtn.textContent = 'Add ' + card.title + ' to cart';
          addBtn.dataset.variantId = String(card.variantId);
        } else if (addBtn) {
          addBtn.classList.add('hidden');
        }
      } else {
        if (msg) {
          msg.textContent =
            'Add ' + missing.length + ' more bundle items and save ' + pctLabel + ' on the set.';
        }
        if (addBtn) addBtn.classList.add('hidden');
      }
    }
  }

  var debouncedRender = debounce(function () {
    fetchCart().then(renderAll);
  }, 150);

  /* ────────────────────────────────────────
     CTA handler
  ──────────────────────────────────────── */

  /** Tolerant variant-id parser — accepts numeric, "ProductVariant/N",
   *  or "gid://shopify/ProductVariant/N". Server should emit numeric;
   *  client stays defensive. */
  function variantIdToNumeric(raw) {
    if (raw == null) return null;
    var m = String(raw).match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  }

  function bindAddOtherClick() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-bundle-add-other]');
      if (!btn) return;
      var variantNumeric = variantIdToNumeric(btn.dataset.variantId);
      if (!variantNumeric) return;
      e.preventDefault();
      btn.disabled = true;
      var origLabel = btn.textContent;
      btn.textContent = 'Adding…';
      addVariantToCart(variantNumeric)
        .then(function () {
          // Tell other modules a cart change happened.
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: SOURCE_LABEL } }));
          return fetchCart();
        })
        .then(renderAll)
        .catch(function (err) {
          console.warn('[' + SOURCE_LABEL + '] add failed:', err);
          btn.textContent = origLabel;
          btn.disabled = false;
        });
    });
  }

  /* ────────────────────────────────────────
     Bootstrap
  ──────────────────────────────────────── */

  function getCustomerBrand() {
    var meta = document.querySelector('meta[name="br-customer-brand"]');
    return meta ? (meta.getAttribute('content') || '').toLowerCase() : null;
  }

  function getProductNumericId() {
    var pdp = document.querySelector('[data-bundle-promo-pdp]');
    if (pdp) {
      var pid = pdp.getAttribute('data-product-id');
      if (pid) return pid;
    }
    var meta = document.querySelector('meta[name="shopify-product-id"]');
    return meta ? meta.getAttribute('content') : null;
  }

  function init() {
    var pdpEl = document.querySelector('[data-bundle-promo-pdp]');
    var cardEls = document.querySelectorAll('[data-bundle-promo-card]');
    // Bail out only when neither surface is rendered (e.g. plain article
    // pages). On /pages/carts cardEls is non-empty even though pdpEl is
    // not — without this, the card-only context would never hydrate.
    if (!pdpEl && !cardEls.length) return;
    var includeDraft =
      window.Shopify && window.Shopify.designMode
        ? '?include_draft=1'
        : (location.search.indexOf('preview_theme_id=') !== -1 ? '?include_draft=1' : '');

    // PromoCompat (assets/promo-compat.js) suppresses incompatible
    // overlapping promos so the picker only sees the surviving winner.
    // Fail-open if the helper is missing.
    var compatReady = (window.PromoCompat && window.PromoCompat.ready) || Promise.resolve(null);
    Promise.all([
      fetchJsonResilient(BR_CONNECT_BASE + ACTIVE_PROMOS_PATH + includeDraft),
      compatReady,
    ])
      .then(function (results) {
        var data = results[0];
        var compat = results[1];
        var customerBrand = getCustomerBrand();
        var productId = getProductNumericId();
        state.handlesFromApi = (data && data.productHandles) || {};
        state.productCards = (data && data.productCards) || {};
        var availablePromos = (data && data.promos || []).filter(function (p) {
          if (!compat || !compat.byId || !compat.byId[p.campaignId]) return true;
          return compat.byId[p.campaignId].applicable;
        });
        // PDP branch: pick a promo that includes this product.
        if (productId) {
          state.promo = pickBundlePromo(availablePromos, productId, customerBrand);
        }
        // Always also check for a cart-mode match (so cart card +
        // drawer slots can render even when the current PDP product
        // isn't part of the bundle).
        return fetchCart().then(function () {
          if (!state.promo && cardEls.length) {
            state.promo = pickBundlePromoForCart(
              availablePromos, customerBrand, state.cartLines,
            );
          }
          if (!state.promo) {
            if (pdpEl) pdpEl.classList.add('hidden');
            return;
          }
          renderAll();
        });
      })
      .catch(function (err) {
        console.warn('[' + SOURCE_LABEL + '] init failed:', err);
        if (pdpEl) pdpEl.classList.add('hidden');
      });

    document.addEventListener('cart:updated', debouncedRender);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') debouncedRender();
    });
    bindAddOtherClick();
    bindSeeBundleClick();
  }

  /* ────────────────────────────────────────
     "See bundle" trigger — opens modal
  ──────────────────────────────────────── */

  function bindSeeBundleClick() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-bundle-see-products]');
      if (!btn) return;
      e.preventDefault();
      openBundleModal();
    });
  }

  function openBundleModal() {
    if (!state.promo || !window.PromoProductsModal) return;
    var promo = state.promo;
    var pctLabel = Math.round((promo.percentage || 0) * 100) + '%';
    var products = promo.bundleNumericIdList
      .map(function (id) {
        var card = state.productCards[id];
        if (!card) return null;
        // Decorate with a saving label so the modal renders the % off
        // pill on every card. Treated as advisory — the Function
        // applies on actual checkout.
        return Object.assign({}, card, {
          savingLabel: 'Save ' + pctLabel + ' as bundle',
        });
      })
      .filter(Boolean);
    var inCart = productIdsInCart();
    var allInCart = promo.bundleNumericIdList.every(function (id) { return inCart.has(id); });
    var sub = allInCart
      ? 'Both bundle items are in your cart — ' + pctLabel + ' off applies automatically at checkout.'
      : 'Add both products together to unlock ' + pctLabel + ' off both at checkout.';
    window.PromoProductsModal.open({
      eyebrow: 'Bundle deal · ' + pctLabel + ' off',
      title: promo.title,
      sub: sub,
      footnote: 'Discount is enforced at checkout — both products must be in cart for it to apply.',
      products: products,
      currentNumericId: getProductNumericId(),
      mode: 'bundle',
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
